// ============================================================================
// BANK STATEMENT SERVICE — import + reconciliation worksheet (ADR-0024 slice ④).
// Flow: parse (multipart preview, saves nothing) → import (commit, hash-deduped)
// → auto-match → per-line match / unmatch / ignore / create-receipt.
// Scoped via X-Entity-ID / X-Business-Unit-ID / X-Financial-Year-ID headers.
// ============================================================================

import { get, post, put } from '../client/axiosClient';
import { bankStatementEndpoints } from '../endpoints/banking.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  ID,
  BankStatementDto,
  BankStatementLineDto,
  BankStatementQueryParams,
  BankStatementLineQueryParams,
  StatementFormatDto,
  ParsedStatementPreview,
  StatementInspection,
  StatementColumnMap,
  ImportStatementRequest,
  IgnoreLineRequest,
  CreateReceiptFromLineRequest,
  SaveStatementFormatRequest,
  ReceiptDto,
  BookEntryDto,
  BookEntryQueryParams,
  MatchSuggestionDto,
  StatementSuggestionsDto,
  ConfirmMatchItem,
  ConfirmMatchesResult,
  StatementMatchDto,
  CreateReceiptVoucherRequest,
  CreatePaymentRequest,
} from '../types';

const toPaginated = <T>(response: { data: ApiResponse<BackendPagedResult<T>> }): PaginatedResponse<T> => {
  const paged = response.data.data;
  return {
    success: response.data.success,
    message: response.data.message,
    data: Array.from(paged.items || []),
    pagination: {
      currentPage: paged.page,
      pageSize: paged.pageSize,
      totalPages: paged.totalPages,
      totalItems: paged.totalCount,
      hasNextPage: paged.hasNext,
      hasPreviousPage: paged.hasPrevious,
    },
  };
};

class BankStatementService {
  /** Paged statement register. */
  async getAll(params?: BankStatementQueryParams): Promise<PaginatedResponse<BankStatementDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(bankStatementEndpoints.list(), params)
      : bankStatementEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<BankStatementDto>>>(endpoint);
    return toPaginated<BankStatementDto>(response);
  }

  /** Full statement including its lines. */
  async getById(id: ID): Promise<BankStatementDto> {
    const response = await get<ApiResponse<BankStatementDto>>(bankStatementEndpoints.getById(id));
    return response.data.data;
  }

  /** Paged worksheet rows (within one statement or across statements). */
  async getLines(params?: BankStatementLineQueryParams): Promise<PaginatedResponse<BankStatementLineDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(bankStatementEndpoints.lines(), params)
      : bankStatementEndpoints.lines();
    const response = await get<ApiResponse<BackendPagedResult<BankStatementLineDto>>>(endpoint);
    return toPaginated<BankStatementLineDto>(response);
  }

  /**
   * Read an uploaded statement (CSV / XLSX / XLS) and return its shape: header row, columns
   * with samples, the date layout and the mapping to start from — the remembered one for these
   * headers, else a suggestion (ADR-0055 §3). Saves nothing. headerRow overrides detection.
   */
  async inspect(file: File, bankLedgerId: string, headerRow?: number): Promise<{ inspection: StatementInspection; message?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bankLedgerId', bankLedgerId);
    if (headerRow != null) formData.append('headerRow', String(headerRow));
    const response = await post<ApiResponse<StatementInspection>>(bankStatementEndpoints.inspect(), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { inspection: response.data.data, message: response.data.message };
  }

  /**
   * Map an uploaded statement with the live column mapping — preview only, saves nothing.
   * Multipart FormData; axios sets the boundary itself.
   */
  async parse(file: File, mapping: StatementColumnMap): Promise<{ preview: ParsedStatementPreview; message?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    // The client defaults Content-Type to application/json (and the interceptor
    // back-fills it), which turns FormData posts into 415s. Declaring multipart
    // explicitly makes axios swap in the real boundary-carrying value — same
    // pattern as the client's own uploadFile helper.
    const response = await post<ApiResponse<ParsedStatementPreview>>(bankStatementEndpoints.parse(), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { preview: response.data.data, message: response.data.message };
  }

  /** Commit the parsed lines as a statement. The message carries the skipped-duplicate count. */
  async import(data: ImportStatementRequest): Promise<{ statement: BankStatementDto; message?: string }> {
    const response = await post<ApiResponse<BankStatementDto>>(bankStatementEndpoints.import(), data);
    return { statement: response.data.data, message: response.data.message };
  }

  // ---- reconciliation (ADR-0055 §4) ----

  /** Suggested matches for every unmatched line, grouped by confidence. Saves nothing. */
  async getSuggestions(statementId: ID): Promise<{ suggestions: StatementSuggestionsDto; message?: string }> {
    const response = await get<ApiResponse<StatementSuggestionsDto>>(bankStatementEndpoints.suggestions(statementId));
    return { suggestions: response.data.data, message: response.data.message };
  }

  /** Scored candidates for one line. */
  async getLineSuggestion(lineId: ID): Promise<MatchSuggestionDto> {
    const response = await get<ApiResponse<MatchSuggestionDto>>(bankStatementEndpoints.lineSuggestion(lineId));
    return response.data.data;
  }

  /** Confirm one or many matches; refusals come back per match. */
  async confirmMatches(matches: ConfirmMatchItem[]): Promise<{ result: ConfirmMatchesResult; message?: string }> {
    const response = await post<ApiResponse<ConfirmMatchesResult>>(bankStatementEndpoints.matches(), { matches });
    return { result: response.data.data, message: response.data.message };
  }

  /** The match a line stands in, both sides. */
  async getLineMatch(lineId: ID): Promise<StatementMatchDto> {
    const response = await get<ApiResponse<StatementMatchDto>>(bankStatementEndpoints.lineMatch(lineId));
    return response.data.data;
  }

  /** Release the line's match (or reopen an ignored line); matched vouchers unlock. */
  async unmatchLine(lineId: ID): Promise<void> {
    await post<ApiResponse<unknown>>(bankStatementEndpoints.unmatchLine(lineId), {});
  }

  // ---- post from a line (ADR-0055 §5) — the voucher is posted and matched in one go ----

  async postReceiptVoucher(lineId: ID, data: CreateReceiptVoucherRequest): Promise<StatementMatchDto> {
    const response = await post<ApiResponse<StatementMatchDto>>(bankStatementEndpoints.postReceiptVoucher(lineId), data);
    return response.data.data;
  }

  async postPaymentVoucher(lineId: ID, data: CreatePaymentRequest): Promise<StatementMatchDto> {
    const response = await post<ApiResponse<StatementMatchDto>>(bankStatementEndpoints.postPaymentVoucher(lineId), data);
    return response.data.data;
  }

  /** Bank charges / direct debit (withdrawal) or interest (deposit) to one account, matched to the line. */
  async postQuick(lineId: ID, data: { ledgerId: string; narration?: string | null; instrumentType?: string | null }): Promise<StatementMatchDto> {
    const response = await post<ApiResponse<StatementMatchDto>>(bankStatementEndpoints.postQuick(lineId), data);
    return response.data.data;
  }

  /** The bank book: entries on a bank ledger, unreconciled by default. */
  async getBookEntries(params: BookEntryQueryParams): Promise<PaginatedResponse<BookEntryDto>> {
    const response = await get<ApiResponse<BackendPagedResult<BookEntryDto>>>(buildEndpointWithQuery(bankStatementEndpoints.bookEntries(), params as any));
    return toPaginated<BookEntryDto>(response);
  }

  async ignoreLine(lineId: ID, data: IgnoreLineRequest): Promise<BankStatementLineDto> {
    const response = await post<ApiResponse<BankStatementLineDto>>(bankStatementEndpoints.ignoreLine(lineId), data);
    return response.data.data;
  }

  /** Create a receipt from an unmatched credit line (same capture shape). */
  async createReceiptFromLine(lineId: ID, data: CreateReceiptFromLineRequest): Promise<ReceiptDto> {
    const response = await post<ApiResponse<ReceiptDto>>(bankStatementEndpoints.createReceiptFromLine(lineId), data);
    return response.data.data;
  }

  // ---- format profiles ----

  async getFormats(bankLedgerId?: string): Promise<StatementFormatDto[]> {
    const endpoint = bankLedgerId
      ? buildEndpointWithQuery(bankStatementEndpoints.formats(), { bankLedgerId })
      : bankStatementEndpoints.formats();
    const response = await get<ApiResponse<StatementFormatDto[]>>(endpoint);
    return response.data.data ?? [];
  }

  async createFormat(data: SaveStatementFormatRequest): Promise<StatementFormatDto> {
    const response = await post<ApiResponse<StatementFormatDto>>(bankStatementEndpoints.formats(), data);
    return response.data.data;
  }

  async updateFormat(id: ID, data: SaveStatementFormatRequest): Promise<StatementFormatDto> {
    const response = await put<ApiResponse<StatementFormatDto>>(bankStatementEndpoints.updateFormat(id), data);
    return response.data.data;
  }
}

export const bankStatementService = new BankStatementService();
export default bankStatementService;
